import { useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" fill="none">
      <path d="M44.5 20H24v8.5h11.8C34.7 33.9 30.1 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22c11 0 21-8 21-22 0-1.3-.2-2.7-.5-4z" fill="#FFC107"/>
      <path d="M6.3 14.7l7 5.1C15.2 16.1 19.3 13 24 13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 16.3 2 9.7 7.4 6.3 14.7z" fill="#FF3D00"/>
      <path d="M24 46c5.5 0 10.5-1.9 14.4-5.1l-6.7-5.5C29.7 36.7 27 37.5 24 37.5c-6 0-10.6-3.1-11.8-8.5l-7 5.4C8.6 41.3 15.8 46 24 46z" fill="#4CAF50"/>
      <path d="M44.5 20H24v8.5h11.8c-.6 2.7-2.2 4.9-4.5 6.4l6.7 5.5C42.4 36.8 45 31.2 45 24c0-1.3-.2-2.7-.5-4z" fill="#1976D2"/>
    </svg>
  );
}

/* ─── Icon components ───────────────────────────────────────────────────── */
function IconCheck({ className = 'w-3.5 h-3.5', style }) {
  return (
    <svg className={className} style={style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
function IconSpinner({ className = 'w-4 h-4' }) {
  return (
    <svg className={`${className} animate-spin`} viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}
function IconEye({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
function IconEyeOff({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

export default function LoginPage() {
  const { login, loginWithGoogle } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';

  const [form,      setForm]      = useState({ email: '', password: '' });
  const [loading,   setLoading]   = useState(false);
  const [gLoading,  setGLoading]  = useState(false);
  const [showPass,  setShowPass]  = useState(false);
  const [fieldErr,  setFieldErr]  = useState({ email: false, password: false });
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const googleClientRef = useRef(null);

  const set = field => e => {
    setForm(f => ({ ...f, [field]: e.target.value }));
    if (fieldErr[field]) setFieldErr(prev => ({ ...prev, [field]: false }));
  };

  const handleGoogleClick = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      toast.error('Google sign-in is not configured');
      return;
    }
    if (!window.google?.accounts?.oauth2) {
      toast.error('Google sign-in unavailable — please try again in a moment');
      return;
    }
    if (!googleClientRef.current) {
      googleClientRef.current = window.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: 'openid profile email',
        callback: async (tokenResponse) => {
          if (tokenResponse.error) {
            toast.error('Google sign-in was cancelled');
            return;
          }
          setGLoading(true);
          try {
            const data = await loginWithGoogle(tokenResponse.access_token);
            toast.success(data.is_new_user ? 'Welcome to CentricQ!' : 'Welcome back!');
            navigate(from, { replace: true });
          } catch (err) {
            toast.error(err.response?.data?.error || 'Google sign-in failed. Please try again.');
          } finally {
            setGLoading(false);
          }
        },
      });
    }
    googleClientRef.current.requestAccessToken();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = { email: !form.email, password: !form.password };
    if (errs.email || errs.password) {
      setFieldErr(errs);
      toast.error('Please enter your email and password');
      return;
    }
    setLoading(true);
    setUnverifiedEmail('');
    try {
      await login(form.email, form.password);
      toast.success('Welcome back!');
      navigate(from, { replace: true });
    } catch (err) {
      if (err.response?.data?.code === 'EMAIL_NOT_VERIFIED') {
        setUnverifiedEmail(err.response.data.email || form.email);
      } else {
        toast.error(err.response?.data?.error || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        *, *::before, *::after { box-sizing: border-box; }

        .ip-input:focus {
          border-color: #1B3A5C !important;
          box-shadow: 0 0 0 3px rgba(27,58,92,0.10) !important;
          outline: none;
        }

        .ip-btn-primary:not(:disabled):hover {
          background: #142d47 !important;
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(27,58,92,0.22);
        }
        .ip-btn-primary { transition: background 200ms, transform 200ms, box-shadow 200ms; }

        .ip-glass {
          background: rgba(255,255,255,0.08);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255,255,255,0.13);
        }

        @keyframes ip-float {
          0%, 100% { transform: translateY(0px) translateX(0px); opacity: 0.35; }
          33%       { transform: translateY(-14px) translateX(6px); opacity: 0.6; }
          66%       { transform: translateY(-6px) translateX(-8px); opacity: 0.45; }
        }
      `}</style>

      {/* ── Left brand panel ─────────────────────────────────────────────── */}
      <div
        className="hidden lg:flex flex-col justify-between flex-shrink-0 px-12 pt-12 pb-10 relative overflow-hidden"
        style={{
          width: 440,
          background: 'linear-gradient(160deg, #0A1929 0%, #0d2238 50%, #1B3A5C 100%)',
        }}
      >
        {/* Floating particles */}
        {[
          { top: '12%', left: '8%',  size: 3, delay: '0s',   dur: '6s'   },
          { top: '28%', left: '72%', size: 2, delay: '1.2s', dur: '8s'   },
          { top: '55%', left: '18%', size: 4, delay: '2.1s', dur: '7s'   },
          { top: '70%', left: '60%', size: 2, delay: '0.5s', dur: '9s'   },
          { top: '40%', left: '45%', size: 3, delay: '3s',   dur: '6.5s' },
          { top: '82%', left: '30%', size: 2, delay: '1.8s', dur: '7.5s' },
          { top: '20%', left: '55%', size: 2, delay: '4s',   dur: '8.5s' },
          { top: '90%', left: '80%', size: 3, delay: '2.5s', dur: '7s'   },
        ].map((p, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              top: p.top, left: p.left,
              width: p.size, height: p.size,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.35)',
              animationName: 'ip-float',
              animationDuration: p.dur,
              animationDelay: p.delay,
              animationTimingFunction: 'ease-in-out',
              animationIterationCount: 'infinite',
            }}
          />
        ))}

        {/* Logo */}
        <Link to="/" className="no-underline relative z-10">
          <span style={{ fontSize: 22, fontWeight: 800, color: '#FFFFFF', letterSpacing: '-0.02em' }}>
            Centric<span style={{ color: '#C1272D' }}>Q</span>
          </span>
        </Link>

        {/* Middle content */}
        <div className="relative z-10">
          {/* Trust chips */}
          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#C1272D', marginBottom: 14 }}>
            Trusted by candidates at
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 36 }}>
            {['ADCB', 'Noon', 'Careem', 'Emaar', 'ENOC', 'FAB'].map(co => (
              <span
                key={co}
                style={{
                  fontSize: 12, fontWeight: 600,
                  padding: '4px 12px',
                  borderRadius: 999,
                  color: 'rgba(255,255,255,0.55)',
                  background: 'rgba(255,255,255,0.07)',
                  border: '1px solid rgba(255,255,255,0.10)',
                }}
              >
                {co}
              </span>
            ))}
          </div>

          <h2 style={{ fontSize: 34, fontWeight: 800, color: '#FFFFFF', letterSpacing: '-0.02em', lineHeight: 1.12, marginBottom: 16 }}>
            Walk into your next interview confident.
          </h2>
          <p style={{ fontSize: 15, lineHeight: 1.8, color: 'rgba(255,255,255,0.5)', marginBottom: 28 }}>
            1,000 role-specific questions, AI feedback on every answer, and a personalised improvement plan built for serious candidates.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              '150+ job roles across 15 industries',
              'MCQ, written & spoken practice',
              'Real-time AI scoring by Claude',
              'Certificate upon completion',
            ].map(item => (
              <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 20, height: 20, borderRadius: '50%',
                  background: 'rgba(193,39,45,0.18)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <IconCheck className="w-3 h-3" style={{ color: '#C1272D' }} />
                </div>
                <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.65)' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom testimonial — glassmorphism card */}
        <div className="ip-glass relative z-10" style={{ borderRadius: 16, padding: 20 }}>
          <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
            {[1,2,3,4,5].map(s => (
              <svg key={s} style={{ width: 14, height: 14 }} viewBox="0 0 24 24" fill="#C1272D">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
            ))}
          </div>
          <p style={{ fontSize: 14, lineHeight: 1.7, fontStyle: 'italic', color: 'rgba(255,255,255,0.65)', marginBottom: 14 }}>
            "The AI feedback was more specific than my university career centre. I landed my role at ADCB 3 weeks later."
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              background: '#C1272D',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 12, fontWeight: 700, flexShrink: 0,
            }}>
              SA
            </div>
            <div>
              <p style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.75)' }}>Sarah Al Mansouri</p>
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>Financial Analyst, ADCB</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Right form panel ──────────────────────────────────────────────── */}
      <div
        className="flex-1 flex items-center justify-center px-6 py-12"
        style={{ background: '#FFFFFF' }}
      >
        <div style={{ width: '100%', maxWidth: 400 }}>

          {/* Mobile logo */}
          <div className="lg:hidden" style={{ textAlign: 'center', marginBottom: 32 }}>
            <Link to="/" className="no-underline">
              <span style={{ fontSize: 22, fontWeight: 800, color: '#1B3A5C', letterSpacing: '-0.02em' }}>
                Centric<span style={{ color: '#C1272D' }}>Q</span>
              </span>
            </Link>
          </div>

          {/* Heading */}
          <div style={{ marginBottom: 28 }}>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.02em', marginBottom: 6 }}>
              Sign in
            </h1>
            <p style={{ fontSize: 15, color: '#64748B' }}>
              Continue your interview preparation
            </p>
          </div>

          {/* Google sign-in button */}
          <button
            type="button"
            onClick={handleGoogleClick}
            disabled={gLoading || loading}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              padding: '13px 16px',
              background: '#FFFFFF',
              border: '1.5px solid #E2E8F0',
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 600,
              color: '#0F172A',
              cursor: (gLoading || loading) ? 'not-allowed' : 'pointer',
              opacity: (gLoading || loading) ? 0.6 : 1,
              transition: 'border-color 150ms, box-shadow 150ms',
              marginBottom: 20,
            }}
            onMouseEnter={e => { if (!gLoading && !loading) e.currentTarget.style.borderColor = '#1B3A5C'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#E2E8F0'; }}
          >
            {gLoading ? <IconSpinner className="w-4 h-4" /> : <GoogleIcon />}
            {gLoading ? 'Signing in…' : 'Continue with Google'}
          </button>

          {/* Divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{ flex: 1, height: 1, background: '#E2E8F0' }} />
            <span style={{ fontSize: 12, fontWeight: 500, color: '#94A3B8', whiteSpace: 'nowrap' }}>or sign in with email</span>
            <div style={{ flex: 1, height: 1, background: '#E2E8F0' }} />
          </div>

          {/* Email not verified banner */}
          {unverifiedEmail && (
            <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: '14px 16px', marginBottom: 4 }}>
              <p style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 700, color: '#92400E' }}>Email not verified</p>
              <p style={{ margin: '0 0 10px', fontSize: 13, color: '#78350F', lineHeight: 1.5 }}>
                Your account needs email verification before you can log in.
              </p>
              <Link
                to="/register"
                state={{ resendEmail: unverifiedEmail }}
                style={{ display: 'inline-block', background: '#1B3A5C', color: '#fff', padding: '8px 16px', borderRadius: 7, fontSize: 13, fontWeight: 700, textDecoration: 'none' }}
              >
                Verify my email
              </Link>
            </div>
          )}

          {/* Form — no card, just fields on white */}
          <form onSubmit={handleSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* Email */}
            <div>
              <label htmlFor="email" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
                Email address
              </label>
              <input
                id="email"
                type="email"
                value={form.email}
                onChange={set('email')}
                placeholder="you@example.com"
                autoComplete="email"
                className="ip-input"
                aria-invalid={fieldErr.email}
                style={{
                  width: '100%', display: 'block',
                  padding: '12px 16px',
                  fontSize: 15,
                  border: `1.5px solid ${fieldErr.email ? '#C1272D' : '#E2E8F0'}`,
                  borderRadius: 10,
                  background: fieldErr.email ? '#FFF5F5' : '#FFFFFF',
                  color: '#0F172A',
                  transition: 'border-color 150ms, box-shadow 150ms',
                }}
              />
            </div>

            {/* Password */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <label htmlFor="password" style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="no-underline"
                  style={{ fontSize: 12, fontWeight: 500, color: '#1B3A5C', transition: 'color 150ms' }}
                  onMouseEnter={e => e.target.style.color = '#C1272D'}
                  onMouseLeave={e => e.target.style.color = '#1B3A5C'}
                >
                  Forgot password?
                </Link>
              </div>
              <div style={{ position: 'relative' }}>
                <input
                  id="password"
                  type={showPass ? 'text' : 'password'}
                  value={form.password}
                  onChange={set('password')}
                  placeholder="Your password"
                  autoComplete="current-password"
                  className="ip-input"
                  aria-invalid={fieldErr.password}
                  style={{
                    width: '100%', display: 'block',
                    padding: '12px 48px 12px 16px',
                    fontSize: 15,
                    border: `1.5px solid ${fieldErr.password ? '#C1272D' : '#E2E8F0'}`,
                    borderRadius: 10,
                    background: fieldErr.password ? '#FFF5F5' : '#FFFFFF',
                    color: '#0F172A',
                    transition: 'border-color 150ms, box-shadow 150ms',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPass(v => !v)}
                  aria-label={showPass ? 'Hide password' : 'Show password'}
                  style={{
                    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', padding: 4,
                    color: '#94A3B8', cursor: 'pointer', transition: 'color 150ms',
                  }}
                  onMouseEnter={e => e.currentTarget.style.color = '#1B3A5C'}
                  onMouseLeave={e => e.currentTarget.style.color = '#94A3B8'}
                >
                  {showPass ? <IconEyeOff className="w-4 h-4" /> : <IconEye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="ip-btn-primary"
              style={{
                width: '100%',
                padding: '14px',
                background: '#1B3A5C',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.55 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              {loading && <IconSpinner className="w-4 h-4" />}
              {loading ? 'Signing in…' : 'Sign in'}
            </button>

          </form>

          {/* Footer link */}
          <p style={{ textAlign: 'center', marginTop: 24, fontSize: 14, color: '#64748B' }}>
            Don't have an account?{' '}
            <Link
              to="/register"
              className="no-underline"
              style={{ fontWeight: 600, color: '#1B3A5C', transition: 'color 150ms' }}
              onMouseEnter={e => e.target.style.color = '#C1272D'}
              onMouseLeave={e => e.target.style.color = '#1B3A5C'}
            >
              Create one free →
            </Link>
          </p>

        </div>
      </div>
    </div>
  );
}
